import clsx from 'clsx';
import { clamp } from 'lodash-es';
import { useEffect, useRef } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Radio, RadioGroup, Stack } from 'src/core/ui';
import { SimplifiedUndercutMode } from 'src/utils/autoPricingUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { SIMPLIFIED_UNDERCUT_MODE_TO_CID } from 'src/utils/constants/contentIdMaps';

import * as styles from './CompAdvancedPricingSettingsContainer.css';

export const CompAdvancedPricingSettingsUndercut = ({
  simplifiedUndercutMode,
  onSimplifiedUndercutModeChange,
  undercutReferenceListingPosition,
  setUndercutReferenceListingPosition,
}: {
  simplifiedUndercutMode: SimplifiedUndercutMode | null;
  onSimplifiedUndercutModeChange: (
    newMode: SimplifiedUndercutMode | null
  ) => void;
  undercutReferenceListingPosition: number | null;
  setUndercutReferenceListingPosition: (newPosition: number | null) => void;
}) => {
  return (
    <RadioGroup
      style={{ alignSelf: 'stretch' }}
      onValueChange={(v) => {
        onSimplifiedUndercutModeChange(v as SimplifiedUndercutMode);
      }}
      value={simplifiedUndercutMode?.toString()}
    >
      <Stack direction="column" gap="m">
        <CompAdvancedPricingSettingsUndercutRadio
          key={0}
          value={SimplifiedUndercutMode.CheapestListing}
          currentValue={simplifiedUndercutMode}
          description={(isChecked) => (
            <Stack
              direction="row"
              gap="s"
              alignItems="center"
              className={clsx(styles.settingOptionTitle, {
                [styles.settingOptionTitleSelected]: isChecked,
              })}
            >
              <div>
                <Content id={ContentId.ReferenceThe} />
              </div>
              <div style={{ textTransform: 'lowercase' }}>
                <Content id={ContentId.CheapestListing} />
              </div>
            </Stack>
          )}
        />

        <CompAdvancedPricingSettingsUndercutRadio
          key={1}
          value={SimplifiedUndercutMode.NthCheapestListing}
          currentValue={simplifiedUndercutMode}
          description={(isChecked, inputRef) => (
            <Stack
              direction="row"
              gap="s"
              alignItems="center"
              className={clsx(styles.settingOptionTitle, {
                [styles.settingOptionTitleSelected]: isChecked,
              })}
            >
              <div>
                <Content id={ContentId.ReferenceThe} />
              </div>

              <div>
                <PosTextField
                  ref={inputRef}
                  disabled={!isChecked}
                  rootProps={{
                    style: {
                      height: '32px',
                      width: '44px',
                      padding: '8px 6px',
                    },
                  }}
                  type="number"
                  value={undercutReferenceListingPosition?.toString() ?? ''}
                  onChange={(e) => {
                    const v = parseInt(e.target.value);
                    const undercutReferenceListingPosition = clamp(v, 1, 10);
                    setUndercutReferenceListingPosition(
                      undercutReferenceListingPosition
                    );
                  }}
                  onBlur={() => {
                    if (!undercutReferenceListingPosition) {
                      setUndercutReferenceListingPosition(2);
                    }
                  }}
                />
              </div>

              <div style={{ textTransform: 'lowercase' }}>
                <Content id={ContentId.CheapestListing} />
              </div>
            </Stack>
          )}
        />

        <CompAdvancedPricingSettingsUndercutRadio
          key={2}
          value={SimplifiedUndercutMode.AvgOfNthCheapestListings}
          currentValue={simplifiedUndercutMode}
          description={(isChecked, inputRef) => (
            <Stack
              direction="row"
              gap="s"
              alignItems="center"
              className={clsx(styles.settingOptionTitle, {
                [styles.settingOptionTitleSelected]: isChecked,
              })}
            >
              <div>
                <Content id={ContentId.AvgOfTheFirst} />
              </div>

              <div>
                <PosTextField
                  ref={inputRef}
                  disabled={!isChecked}
                  rootProps={{
                    style: {
                      height: '32px',
                      width: '44px',
                      padding: '8px 6px',
                    },
                  }}
                  type="number"
                  value={undercutReferenceListingPosition?.toString() ?? ''}
                  onChange={(e) => {
                    const v = parseInt(e.target.value);
                    const undercutReferenceListingPosition = clamp(v, 1, 10);
                    setUndercutReferenceListingPosition(
                      undercutReferenceListingPosition
                    );
                  }}
                  onBlur={() => {
                    if (!undercutReferenceListingPosition) {
                      setUndercutReferenceListingPosition(2);
                    }
                  }}
                />
              </div>

              <div style={{ textTransform: 'lowercase' }}>
                <Content id={ContentId.CheapestListings} />
              </div>
            </Stack>
          )}
        />
      </Stack>
    </RadioGroup>
  );
};

const CompAdvancedPricingSettingsUndercutRadio = ({
  value,
  currentValue,
  description,
}: {
  value: SimplifiedUndercutMode;
  currentValue: SimplifiedUndercutMode | null;
  description: (
    isChecked: boolean,
    inputRef: React.ForwardedRef<HTMLInputElement | null>
  ) => React.ReactNode;
}) => {
  const isChecked = currentValue === value;
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isChecked && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isChecked]);

  return (
    <Stack
      className={clsx(styles.settingOptionContainer, {
        [styles.settingOptionContainerSelected]: isChecked,
      })}
    >
      <Radio
        value={value}
        label={
          <Stack direction="column" gap="s">
            <div
              className={clsx(styles.settingOptionTitle, {
                [styles.settingOptionTitleSelected]: isChecked,
              })}
            >
              <Content id={SIMPLIFIED_UNDERCUT_MODE_TO_CID[value]} />
            </div>

            {description(isChecked, inputRef)}
          </Stack>
        }
      />
    </Stack>
  );
};
